import { Abas } from '@core/components/Abas';
import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { cancelarAlert, simpleAlert } from 'app/services/alerts';
import { removeCpfCnpjMask } from 'app/utils/mask';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { FaUsers } from 'react-icons/fa';
import { ClienteContext } from '../../../ClienteContext';
import { clienteModais } from '../clienteModais';
import { useClienteValidation } from './clienteValidation';
import { TabDadosGeraisCliente } from './components';
import TabContaBancariaCliente from './components/TabContaBancariaCliente';
import TabContatoCliente from './components/TabContatoCliente';
import TabEnderecoCliente from './components/TabEnderecoCliente';

const clienteAbas = {
  dadosGerais: 0,
  endereco: 1,
  contato: 2,
  contaBancaria: 3,
};

const CadastroClienteModal = ({ onSubmit, fetchPapel }) => {
  const [cliente] = useContext(ClienteContext).clienteAtual;
  const [modal, setModal] = useContext(ClienteContext).modal;
  const [search] = useContext(ClienteContext).search;
  const [page] = useContext(ClienteContext).page;
  const [size] = useContext(ClienteContext).size;
  const [abaSelecionada, setAbaSelecionada] = useState(clienteAbas.dadosGerais);

  const clientePessoaFisicaInitialValues = {
    cpf: cliente?.pessoa?.pessoaFisica?.cpf || '',
    identidade: cliente?.pessoa?.pessoaFisica?.identidade || '',
    dataNascimento: cliente?.pessoa?.pessoaFisica?.dataNascimento
      ? moment(cliente?.pessoa?.pessoaFisica?.dataNascimento)
      : null,
  };

  const clienteEmpresaEstrangeiraInitialValues = {
    identificadorEstrangeiro: cliente?.pessoa?.empresaEstrangeira?.identificadorEstrangeiro || '',
  };

  const clientePessoaJuridicaInitialValues = {
    indicadorInscricaoEstadual: cliente?.pessoa?.pessoaJuridica?.indicadorInscricaoEstadual || '',
    cnpj: cliente?.pessoa?.pessoaJuridica?.cnpj || '',
    razaoSocial: cliente?.pessoa?.pessoaJuridica?.razaoSocial || '',
    dataFundacao: cliente?.pessoa?.pessoaJuridica?.dataFundacao
      ? moment(cliente.pessoa.pessoaJuridica.dataFundacao)
      : null,
    inscricaoEstadual: cliente?.pessoa?.pessoaJuridica?.inscricaoEstadual || '',
    inscricaoMunicipal: cliente?.pessoa?.pessoaJuridica?.inscricaoMunicipal || '',
    suframa: cliente?.pessoa?.pessoaJuridica?.suframa || '',
  };

  const enderecoInitialValues = {
    id: cliente?.pessoa?.endereco?.id || '',
    pais: cliente?.pessoa?.endereco?.pais || '',
    cep: cliente?.pessoa?.endereco?.cep || '',
    logradouro: cliente?.pessoa?.endereco?.logradouro || '',
    numero: cliente?.pessoa?.endereco?.numero || '',
    complemento: cliente?.pessoa?.endereco?.complemento || '',
    uf: cliente?.pessoa?.endereco?.uf || {},
    cidade: cliente?.pessoa?.endereco?.cidade || '',
    bairro: cliente?.pessoa?.endereco?.bairro || '',
    referencia: cliente?.pessoa?.endereco?.referencia || '',
  };

  const contatoInitialValues = [
    {
      id: cliente?.pessoa?.contatos?.[0]?.id || '',
      email: cliente?.pessoa?.contatos?.[0]?.email || '',
      nomeReferencia: cliente?.pessoa?.contatos?.[0]?.nomeReferencia || '',
      codigoPais: cliente?.pessoa?.contatos?.[0]?.codigoPais || '',
      numero: cliente?.pessoa?.contatos?.[0]?.numero || '',
      telefonesAdicionais: cliente?.pessoa?.contatos?.[0]?.telefonesAdicionais || [],
      emailsAdicionais: cliente?.pessoa?.contatos?.[0]?.emailsAdicionais || [],
    },
  ];

  const contaInitialValues = {
    id: cliente?.pessoa?.conta?.id || '',
    contaBancaria: cliente?.pessoa?.conta?.contaBancaria || {},
    descricao: cliente?.pessoa?.conta?.descricao || '',
    tipo: cliente?.pessoa?.conta?.tipo || '',
  };

  const initialValues = {
    id: cliente?.id || '',
    codigoCliente: cliente?.codigoCliente || '',
    pessoa: {
      id: cliente?.pessoa?.id || '',
      nome: cliente?.pessoa?.nome || '',
      tipoPessoa: cliente?.pessoa?.tipoPessoa || 'JURIDICA',
      inscricaoMunicipal: cliente?.pessoa?.inscricaoMunicipal,
      pessoaFisica: clientePessoaFisicaInitialValues,
      pessoaJuridica: clientePessoaJuridicaInitialValues,
      empresaEstrangeira: clienteEmpresaEstrangeiraInitialValues,
      contatos: contatoInitialValues,
      cpfCnpj: clientePessoaFisicaInitialValues.cpf
        ? removeCpfCnpjMask(clientePessoaFisicaInitialValues.cpf)
        : removeCpfCnpjMask(clientePessoaJuridicaInitialValues.cnpj),
      endereco: enderecoInitialValues,
      conta: contaInitialValues,
    },
    principalAtividadeEconomica: cliente?.principalAtividadeEconomica,
    observacoes: cliente?.observacoes,
    possuiVinculo: cliente?.possuiVinculo,
    optanteSimples: cliente?.optanteSimples || false,
    contribuinteIbs: cliente?.contribuinteIbs ?? null,
    contribuinteCbs: cliente?.contribuinteCbs ?? null,
    tipoIndicadorOperacao: cliente?.tipoIndicadorOperacao || '',
  };

  const clienteSchema = useClienteValidation();

  const close = () => {
    formik.resetForm(initialValues);
    setAbaSelecionada(clienteAbas.dadosGerais);
    setModal('');
  };

  const filterEmailAdicional = (emailAdicional) => {
    return !!emailAdicional?.email;
  };

  const filterTelefoneAdicional = (telefoneAdicional) => {
    return !!telefoneAdicional?.numero;
  };

  const handleSubmit = async (registro) => {
    if (!formik?.dirty) {
      close();
      return;
    }

    const cliente = JSON.parse(JSON.stringify(registro));

    if (cliente?.principalAtividadeEconomica?.id) {
      cliente.principalAtividadeEconomicaId = cliente.principalAtividadeEconomica.id;
      delete cliente.principalAtividadeEconomica;
    }
    if (cliente.pessoa?.conta?.contaBancaria?.tipoConta) {
      cliente.pessoa.conta.tipoConta = cliente.pessoa.conta.contaBancaria.tipoConta;
    }

    if (cliente?.pessoa?.tipoPessoa === 'FISICA') {
      delete cliente.pessoa.pessoaJuridica;
      delete cliente.pessoa?.endereco?.pais;
      delete cliente.pessoa?.empresaEstrangeira;
    } else if (cliente?.pessoa?.tipoPessoa === 'JURIDICA') {
      delete cliente.pessoa.pessoaFisica;
      delete cliente.pessoa?.endereco?.pais;
      delete cliente.pessoa?.empresaEstrangeira;
    } else if (cliente?.pessoa?.tipoPessoa === 'ESTRANGEIRA') {
      delete cliente.pessoa.pessoaFisica;
      delete cliente.pessoa.pessoaJuridica;
    }

    // tipo conta é obrigatório se algum dado de conta for preenchido.
    // se n tiver tipoConta, não possui nenhum dado de conta
    if (!cliente?.pessoa.conta?.tipoConta) {
      delete cliente.pessoa.conta;
    }

    // se n tem nenhum dado preenchido de endereço
    if (cliente.pessoa.endereco && !Object.values(cliente.pessoa.endereco).some((value) => !!value)) {
      delete cliente.pessoa.endereco;
    }

    if (cliente.pessoa.contatos?.length > 0) {
      for (let i = 0; i < cliente.pessoa.contatos.length; i++) {
        const contato = cliente.pessoa.contatos[i];

        if (contato?.emailsAdicionais?.length > 0) {
          contato.emailsAdicionais = contato.emailsAdicionais.filter(filterEmailAdicional);
        }

        if (contato?.telefonesAdicionais?.length > 0) {
          contato.telefonesAdicionais = contato.telefonesAdicionais.filter(filterTelefoneAdicional);
        }
      }
    }

    const res = await onSubmit(cliente, { q: search, page, size });

    if (res) {
      if (res.data?.possuiCobranca) {
        await simpleAlert({
          opcoes: {
            title: 'Os boletos que já foram gerados não serão afetados com a alteração feita.',
          },
        });
      }

      close();
    }
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const formik = useFormik({
    validationSchema: clienteSchema,
    initialValues,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleChangeTipoPessoa = (tipoPessoa) => {
    if (tipoPessoa === 'FISICA') {
      formik.setFieldValue('pessoaJuridica', null);
      formik.setFieldValue('pessoa.pessoaFisica', clientePessoaFisicaInitialValues);
      formik.setFieldValue('pessoa.contatos[0].codigoPais', null);
    } else if (tipoPessoa === 'JURIDICA') {
      formik.setFieldValue('pessoaFisica', null);
      formik.setFieldValue('pessoa.pessoaJuridica', clientePessoaJuridicaInitialValues);
      formik.setFieldValue('pessoa.contatos[0].codigoPais', null);
    } else if (tipoPessoa === 'ESTRANGEIRA') {
      formik.setFieldValue('pessoaJuridica', null);
      formik.setFieldValue('pessoaFisica', null);
      formik.setFieldValue('pessoa.endereco.cep', null);
      formik.setFieldValue('pessoa.endereco.uf', null);
      formik.setFieldValue('pessoa.endereco.cidade', null);
      formik.setFieldValue('pessoa.empresaEstrangeira', clienteEmpresaEstrangeiraInitialValues);
    }
  };

  useEffect(() => {
    formik.resetForm(initialValues);
    // Efeito reativo apenas ao gatilho acima; incluir as demais reiniciaria o formulário durante a edição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente]);

  return (
    <SimpleDialog fullScreen open={modal === clienteModais.cadastroCliente} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          fullScreen
          header={{
            title: 'Cadastro de cliente',
            icon: <FaUsers />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleClose}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          contentToolbar={
            <Abas
              value={abaSelecionada}
              onChange={setAbaSelecionada}
              tabs={[
                {
                  label: 'Dados gerais',
                  hasError: () => {
                    return false;
                  },
                },
                {
                  label: 'Endereço',
                  hasError: () => {
                    return false;
                  },
                },
                {
                  label: 'Contato',
                  hasError: () => {
                    return false;
                  },
                },
                {
                  label: 'Conta bancária',
                  hasError: () => {
                    return false;
                  },
                },
              ]}
            />
          }
          content={
            <div className="h-full max-h-full w-full">
              {abaSelecionada === clienteAbas.dadosGerais && (
                <TabDadosGeraisCliente onChangeTipoPessoa={handleChangeTipoPessoa} fetchPapel={fetchPapel} />
              )}
              {abaSelecionada === clienteAbas.endereco && <TabEnderecoCliente />}
              {abaSelecionada === clienteAbas.contato && <TabContatoCliente />}
              {abaSelecionada === clienteAbas.contaBancaria && (
                <TabContaBancariaCliente
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  values={formik.values}
                  errors={formik.errors}
                  touched={formik.touched}
                  submitCount={formik.submitCount}
                />
              )}
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default CadastroClienteModal;
